import { CSSProperties, useMemo } from 'react';
import { useScreenDetector } from '../../../hooks';
import { RenderOn } from '../../RenderOn/RenderOn';
import { Link, LinkProps } from '../../Link/Link';
import Logo, { LogoProps } from '../../Logo/Logo';
import { nanoid } from 'nanoid';

import styles from './PlayerPortalsFooter.module.scss';

export type PlayerPortalsFooterProps = {
  links?: LinkProps[];
  copyrightNotice?: string | undefined;
  organizationLogos?: LogoProps[];
  desktopBackgroundImage?: string;
  mobileBackgroundImage?: string;
  kontentItemId?: string;
};

export const PlayerPortalsFooter = ({
  links,
  copyrightNotice,
  organizationLogos,
  desktopBackgroundImage,
  mobileBackgroundImage,
  kontentItemId,
}: PlayerPortalsFooterProps) => {
  const { isMobile, isTablet } = useScreenDetector();

  const bgImageUrl = useMemo(() => {
    return isMobile ? mobileBackgroundImage : desktopBackgroundImage;
  }, [isMobile, mobileBackgroundImage, desktopBackgroundImage]);

  const inlineStyle: CSSProperties = {
    backgroundImage: bgImageUrl && `url(${bgImageUrl})`,
  };

  return (
    <footer
      data-testid="Footer"
      className={`${styles.footer} ${
        bgImageUrl ? styles['footer--withBg'] : ''
      }`}
      style={inlineStyle}
      data-kontent-item-id={kontentItemId}
    >
      <div className={styles.container}>
        {isMobile || isTablet ? (
          <>
            <LinksBox footerLinks={links} />
            <OrganizationLogosBox logos={organizationLogos} />
            <CopyrightsBox text={copyrightNotice} />
          </>
        ) : (
          <>
            <div className={styles['desktop-left-box']}>
              <LinksBox footerLinks={links} />
              <CopyrightsBox text={copyrightNotice} />
            </div>
            <div className={styles['desktop-right-box']}>
              <OrganizationLogosBox logos={organizationLogos} />
            </div>
          </>
        )}
      </div>
    </footer>
  );
};

const LinksBox = ({ footerLinks }: { footerLinks?: LinkProps[] }) =>
  footerLinks?.length ? (
    <div className={styles['links']}>
      {footerLinks.map((link, index) => (
        <span key={nanoid(8)}>
          {index !== 0 && (
            <span className={styles['link-separator']}> &#x25CF; </span>
          )}
          <Link {...link} hideIcon />
        </span>
      ))}
    </div>
  ) : null;

const OrganizationLogosBox = ({ logos }: { logos?: LogoProps[] }) =>
  logos?.length ? (
    <div className={styles['organization-box']}>
      {logos?.map(logo => (
        <Logo
          key={`${logo?.logoDescription}-${nanoid(8)}`}
          resource={logo?.resource}
          logoDescription={logo?.logoDescription}
          logoImage={logo?.logoImage}
        />
      ))}
    </div>
  ) : null;

const CopyrightsBox = ({ text }: { text?: string | undefined }) => (
  <RenderOn condition={!!text}>
    <div className={styles['copyrights-box']}>
      <p>{text}</p>
    </div>
  </RenderOn>
);
